<template>
	<div>
		<el-select
			ref="select"
			:value="selected"
			filterable
			:disabled="creating"
			:placeholder="$t('placeholder')"
			@change="onChange">
			<!-- created keeps this option visible while searching (element-ui internal flag, same use as in SupplierSelect) -->
			<el-option
				:key="ADD"
				:label="$t('add_purchase_type')"
				:created="true"
				:value="ADD" />
			<el-option
				v-if="pendingName"
				:key="PENDING"
				:label="`${pendingName} (${$t('pending_creation')})`"
				:value="PENDING" />
			<el-option
				v-for="purchaseType in options"
				:key="purchaseType.purchaseTypeId"
				:label="purchaseType.label"
				:value="purchaseType.purchaseTypeId" />
		</el-select>

		<create-purchase-type-dialog
			v-model="showDialog"
			:purchase-types="labelledPurchaseTypes"
			:initial-name="pendingName"
			@select="onDialogSelect"
			@create="onDialogCreate" />
	</div>
</template>

<script>
import { TRANSLATION_KEY_BY_DEFAULT_TYPE_ID } from "@/pages/CampaignManager/FormItems/purchaseTypes";
import CreatePurchaseTypeDialog from "@/pages/CampaignManager/FormItems/CreatePurchaseTypeDialog";

const ADD = "ADD_PURCHASE_TYPE";
const PENDING = "PENDING_NEW_PURCHASE_TYPE";

// Purchase type selector of the backlink form. `value` is the type's name (stored in
// backlink_type), `purchaseTypeId` its id (sent to netlinking-sourcing, never stored): both come
// from the same list item, so they are consistent by construction.
// It offers the default types and those of the supplier's offers on the source domain; any other
// type is picked, or a new one named, through the dialog.
export default {
	components: {
		CreatePurchaseTypeDialog
	},
	props: {
		value: {
			type: String,
			default: null
		},
		purchaseTypeId: {
			type: String,
			default: null
		},
		offerPurchaseTypeIds: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			ADD,
			PENDING,
			purchaseTypes: [],
			pendingName: null,
			creating: false,
			showDialog: false
		};
	},
	computed: {
		selected() {
			if (this.pendingName) {
				return PENDING;
			}
			// An unresolved stored name is shown as-is until a type is picked.
			return this.purchaseTypeId || this.value || null;
		},
		labelledPurchaseTypes() {
			return this.purchaseTypes.map((purchaseType) => ({ ...purchaseType, label: this.label(purchaseType) }));
		},
		// The selected type stays listed even when it is neither a default nor an offer's type.
		options() {
			return this.labelledPurchaseTypes.filter(
				(purchaseType) => TRANSLATION_KEY_BY_DEFAULT_TYPE_ID[purchaseType.purchaseTypeId] || this.offerPurchaseTypeIds.includes(purchaseType.purchaseTypeId) || purchaseType.purchaseTypeId === this.purchaseTypeId
			);
		}
	},
	watch: {
		purchaseTypes() {
			this.resolveStoredName();
		},
		// A new form (this component outlives el-dialog openings) resets the value and the id: drop
		// an abandoned pending name and resolve the stored one again.
		value(value) {
			if (this.pendingName && value !== this.pendingName) {
				this.pendingName = null;
			}
			this.resolveStoredName();
		},
		purchaseTypeId() {
			this.resolveStoredName();
		}
	},
	mounted() {
		this.fetchPurchaseTypes();
	},
	methods: {
		// Default types are translated, custom types are shown under their name as-is.
		label(purchaseType) {
			const key = TRANSLATION_KEY_BY_DEFAULT_TYPE_ID[purchaseType.purchaseTypeId];
			return key ? this.$t(key) : purchaseType.name;
		},
		findByName(name) {
			const wanted = (name || "").trim().toLowerCase();
			if (!wanted) {
				return null;
			}
			return this.purchaseTypes.find((purchaseType) => purchaseType.name.toLowerCase() === wanted || this.label(purchaseType).toLowerCase() === wanted) || null;
		},
		// The edit form only knows the stored name: its id is looked up once the list is loaded.
		resolveStoredName() {
			if (this.purchaseTypeId || this.pendingName || !this.value) {
				return;
			}
			const purchaseType = this.findByName(this.value);
			if (purchaseType) {
				this.$emit("purchase-type-change", { purchaseTypeId: purchaseType.purchaseTypeId, userSelected: false });
			}
		},
		onChange(value) {
			// `selected` is bound one-way: picking ADD leaves the current selection displayed.
			if (value === ADD) {
				this.showDialog = true;
				return;
			}
			if (value !== PENDING) {
				this.pick(this.purchaseTypes.find((candidate) => candidate.purchaseTypeId === value));
			}
		},
		pick(purchaseType) {
			this.pendingName = null;
			this.$emit("input", purchaseType.name);
			this.$emit("purchase-type-change", { purchaseTypeId: purchaseType.purchaseTypeId, userSelected: true });
		},
		onDialogSelect(purchaseType) {
			this.showDialog = false;
			this.pick(purchaseType);
		},
		// A new name is only created on submit, see commitPendingPurchaseType.
		onDialogCreate(name) {
			this.showDialog = false;
			this.pendingName = name;
			this.$emit("input", name);
			this.$emit("purchase-type-change", { purchaseTypeId: null, userSelected: true });
			// el-select reads the label when `value` changes, before the pending option exists:
			// refresh it once rendered, or the raw PENDING value shows.
			this.$nextTick(() => this.$refs.select.setSelected());
		},
		// Called by the parent form right before it submits, so the type is only created once the
		// whole backlink is validated: a purchase type is global reference data, and its deletion
		// is refused as soon as an offer carries it.
		commitPendingPurchaseType() {
			if (!this.pendingName) {
				return Promise.resolve(this.purchaseTypeId);
			}
			this.creating = true;
			return this.$apiV2
				.post(
					"/netlinking-sourcing/purchase-types",
					{
						data: {
							type: "CREATE_OR_UPDATE_PURCHASE_TYPES",
							attributes: {
								purchaseTypes: [{ name: this.pendingName }]
							}
						}
					},
					{ headers: { "Content-Type": "application/vnd.api+json" } }
				)
				.then((response) => {
					const created = response.data.data.attributes;
					const purchaseType = created.find((candidate) => candidate.name.toLowerCase() === this.pendingName.toLowerCase()) || created[0];
					this.purchaseTypes.push(purchaseType);
					this.pendingName = null;
					this.$emit("input", purchaseType.name);
					this.$emit("purchase-type-change", { purchaseTypeId: purchaseType.purchaseTypeId, userSelected: false });
					return purchaseType.purchaseTypeId;
				})
				.catch((err) => {
					console.error(err);
					throw err;
				})
				.finally(() => {
					this.creating = false;
				});
		},
		fetchPurchaseTypes() {
			this.$apiV2
				.get("/netlinking-sourcing/purchase-types")
				.then((response) => {
					this.purchaseTypes = response.data.data.attributes;
				})
				.catch((err) => console.error(err));
		}
	}
};
</script>

<i18n>
{
  "en": {
    "redaction": "Advertorial",
    "sponsored": "Sponsored",
    "existing": "Link insertion",
    "placeholder": "Select a purchase type",
    "add_purchase_type": "+ Add a purchase type",
    "pending_creation": "will be created on save"
  },
  "fr": {
    "redaction": "Publi-rédactionnel",
    "sponsored": "Sponsorisé",
    "existing": "Insertion de lien",
    "placeholder": "Choisir un type d'achat",
    "add_purchase_type": "+ Ajouter un type d'achat",
    "pending_creation": "sera créé à l'enregistrement"
  },
  "de": {
    "redaction": "Advertorial",
    "sponsored": "Gesponserter",
    "existing": "Linkeinfügung",
    "placeholder": "Kaufart auswählen",
    "add_purchase_type": "+ Kaufart hinzufügen",
    "pending_creation": "wird beim Speichern erstellt"
  }
}
</i18n>
